import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ConversionsVesReportService,
  VesSwapReportItem,
  VesSwapReportSummary,
  VesSwapReportPagination,
  VesSwapReportQueryParams,
} from '../../services/conversions-ves-report.service';

@Component({
  selector: 'app-report-usdt-ves-general',
  templateUrl: './report-usdt-ves-general.component.html',
  styleUrls: ['./report-usdt-ves-general.component.sass'],
})
export class ReportUsdtVesGeneralComponent implements OnInit, OnDestroy {
  swaps: VesSwapReportItem[] = [];
  summary: VesSwapReportSummary | null = null;
  pagination: VesSwapReportPagination = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  };

  filterForm: FormGroup;
  loading = false;
  errorMessage = '';
  successMessage = '';
  expandedCardId: number | null = null;

  private destroy$ = new Subject<void>();

  pageSizeOptions = [10, 20, 50, 100];

  Math = Math;

  displayedColumns = [
    'userId',
    'userName',
    'userIdentity',
    'userLocation',
    'usdtAmount',
    'vesRate',
    'vesTotal',
    'swapDate',
  ];

  constructor(
    private vesReportService: ConversionsVesReportService,
    private fb: FormBuilder,
  ) {
    this.filterForm = this.fb.group({
      userId: [''],
      startDate: [''],
      endDate: [''],
    });
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  ngOnInit(): void {
    this.vesReportService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.loading = loading;
      });

    this.loadReport();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadReport(): void {
    this.errorMessage = '';
    this.successMessage = '';

    const params: VesSwapReportQueryParams = {
      page: this.pagination.page,
      limit: this.pagination.limit,
      ...this.getFiltersFromForm(),
    };

    this.vesReportService
      .getVesSwapsReport(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.swaps = response.swaps;
          this.summary = response.summary;
          this.pagination = response.pagination;
        },
        error: (error) => {
          this.errorMessage =
            error.message || 'Error al cargar el reporte de swaps';
        },
      });
  }

  private getFiltersFromForm(): Partial<VesSwapReportQueryParams> {
    const filters: Partial<VesSwapReportQueryParams> = {};
    const formValue = this.filterForm.value;

    if (formValue.userId && formValue.userId.trim()) {
      filters.userId = formValue.userId.trim();
    }
    if (formValue.startDate) {
      const date = new Date(formValue.startDate);
      filters.startDate = date.toISOString().split('T')[0];
    }
    if (formValue.endDate) {
      const date = new Date(formValue.endDate);
      filters.endDate = date.toISOString().split('T')[0];
    }

    return filters;
  }

  applyFilters(): void {
    this.pagination.page = 1;
    this.loadReport();
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.pagination.page = 1;
    this.loadReport();
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.pagination.totalPages) {
      return;
    }
    this.pagination.page = page;
    this.loadReport();
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pagination.limit = parseInt(target.value, 10);
    this.pagination.page = 1;
    this.loadReport();
  }

  onPaginatorChange(event: any): void {
    this.pagination.page = event.pageIndex + 1;
    this.pagination.limit = event.pageSize;
    this.loadReport();
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    const currentPage = this.pagination.page;
    const totalPages = this.pagination.totalPages;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 3) {
        endPage = maxPagesToShow;
      }
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxPagesToShow + 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  }

  exportToExcel(): void {
    this.errorMessage = '';
    this.successMessage = '';

    const params: VesSwapReportQueryParams = this.getFiltersFromForm();

    this.vesReportService
      .exportToExcel(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const filename = `swaps-usdt-ves-${new Date().toISOString().split('T')[0]}.xlsx`;
          this.vesReportService.downloadExcelFile(blob, filename);
          this.successMessage = 'Archivo exportado exitosamente';
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        },
        error: (error) => {
          this.errorMessage =
            error.message || 'Error al exportar el reporte a Excel';
        },
      });
  }

  formatUSDT(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(amount);
  }

  formatVES(amount: number): string {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-VE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
}
